import { format, isValid, parse } from "date-fns";

// Raw PCRS keys whose humanized form reads poorly — spell these out.
const LABEL_OVERRIDES = {
  dob: "Date of Birth",
  doctorNum: "GP GMS Number",
  gpGmsNumber: "GP GMS Number",
  allPatientGmsNumbers: "All Active GMS Cards",
  currentActiveGmsCard: "Current Active GMS Card",
};

// Words that should stay all-caps after humanizing camelCase keys.
const ACRONYMS = {
  Gms: "GMS",
  Gp: "GP",
  Ppp: "PPP",
  Ocf: "OCF",
  Pp: "PP",
};

// Render Patient details in a deliberate order so related fields line up.
export const PATIENT_FIELD_ORDER = [
  "dob",
  "gpGmsNumber",
  "currentActiveGmsCard",
  "allPatientGmsNumbers",
  "currentProgramme",
  "paidPppClaims",
  "paidOcfClaims",
  "numberOfConditionsOnLastClaim",
  "registrationDate",
  "mostRecentExamDate",
  "secondMostRecentExamDate",
];

const humanizeKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (character) => character.toUpperCase())
    .trim()
    .split(" ")
    .map((word) => ACRONYMS[word] ?? word)
    .join(" ");

export const toPcrsLabel = (key) => LABEL_OVERRIDES[key] ?? humanizeKey(key);

// Internal/audit fields carried on the scheme-check object but never shown to
// the user: the raw SOAP dump and the parsed object (renders as "[object Object]").
const HIDDEN_PCRS_KEYS = ["rawXml", "parsedResult"];

// PCRS sends dates in either of these formats (the first with a trailing Z).
const INBOUND_DATE_FORMATS = ["yyyy-MM-dd", "dd/MM/yyyy"];
const OUTPUT_DATE_FORMAT = "dd/MM/yyyy";

// Normalise any date-shaped value to dd/MM/yyyy, dropping a trailing Z.
// Returns null when the value is not a date.
const formatDateValue = (text) => {
  const value = text.replace(/Z$/i, "").trim();

  for (const inboundFormat of INBOUND_DATE_FORMATS) {
    const parsed = parse(value, inboundFormat, new Date());
    if (isValid(parsed)) return format(parsed, OUTPUT_DATE_FORMAT);
  }

  return null;
};

// PCRS uses the literal "NONE" for absent programme / claim / date values.
const NONE_PLACEHOLDER = /^none$/i;

// PCRS scheme-type codes → human labels. getSchemeTypes isn't available, so map
// the known codes here (V = Visitation Card is a best guess per PCRS support).
// Unknown codes fall through to the raw value.
const SCHEME_TYPE_LABELS = {
  G: "GMS",
  V: "Visitation Card",
};

// SCREAMING_SNAKE enum values (e.g. VALID, NO_MEDICAL_CARD) read better as Title
// Case — except genuine acronyms, which must stay upper-case.
const ENUM_ACRONYMS = new Set([
  "OCF",
  "PP",
  "PPP",
  "CDM",
  "GMS",
  "GP",
  "HAA",
  "DVC",
]);
const SCREAMING_ENUM = /^[A-Z][A-Z_]+$/;

const prettifyEnum = (text) => {
  if (!SCREAMING_ENUM.test(text) || ENUM_ACRONYMS.has(text)) return text;

  return text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Shown for empty / absent / "NONE" values.
export const PCRS_EMPTY_PLACEHOLDER = "-";

const formatScalar = (value) => {
  if (value === null || value === undefined || value === "")
    return PCRS_EMPTY_PLACEHOLDER;

  if (typeof value === "string") {
    const trimmed = value.trim();
    // Show a clean dash instead of shouting "NONE".
    if (trimmed === "" || NONE_PLACEHOLDER.test(trimmed))
      return PCRS_EMPTY_PLACEHOLDER;

    const asDate = formatDateValue(trimmed);
    if (asDate) return asDate;

    return prettifyEnum(trimmed);
  }

  return String(value);
};

export const formatPcrsValue = (value, key) => {
  // Scheme-type code → label (G = GMS, V = Visitation Card, …); unknown codes
  // fall through to the raw value.
  if (key === "schemeType" && typeof value === "string") {
    const code = value.trim().toUpperCase();
    if (SCHEME_TYPE_LABELS[code]) return SCHEME_TYPE_LABELS[code];
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map(formatScalar).join(", ")
      : PCRS_EMPTY_PLACEHOLDER;
  }

  return formatScalar(value);
};

export const isTruthyPcrsFlag = (value) => value === true || value === "true";
export const isFalsyPcrsFlag = (value) => value === false || value === "false";

// Yes/No rendering covers real booleans ("true"/"false") and the PCRS
// "YES"/"NO" claim strings — all shown as a compact Yes/No pill.
export const isYesValue = (value) =>
  value === true ||
  (typeof value === "string" && /^(true|yes)$/i.test(value.trim()));

export const isNoValue = (value) =>
  value === false ||
  (typeof value === "string" && /^(false|no)$/i.test(value.trim()));

// Object entries ordered by `order` first, with any remaining keys appended.
// Internal-only keys (see HIDDEN_PCRS_KEYS) are always excluded.
export const orderedPcrsEntries = (source, order) => {
  const data = source ?? {};
  const keys = Object.keys(data).filter((key) => !HIDDEN_PCRS_KEYS.includes(key));

  if (!order) return keys.map((key) => [key, data[key]]);

  const inOrder = order.filter(
    (key) => key in data && !HIDDEN_PCRS_KEYS.includes(key),
  );
  const rest = keys.filter((key) => !order.includes(key));

  return [...inOrder, ...rest].map((key) => [key, data[key]]);
};
